import { Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdminUser } from "@/lib/api";

interface Props {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailDialog({ userId, open, onOpenChange }: Props) {
  const { data: user, isLoading, isError } = useAdminUser(open ? userId : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
        </DialogHeader>
        {isError ? (
          <p className="text-destructive py-8 text-center text-sm">Failed to load user details.</p>
        ) : isLoading || !user ? (
          <div className="flex justify-center py-8">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p className="text-muted">Name</p>
              <p>{`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "(no name)"}</p>
              <p className="text-muted">Email</p>
              <p>{user.email}</p>
              <p className="text-muted">Role</p>
              <p>{user.role}</p>
              <p className="text-muted">Joined</p>
              <p>{new Date(user.created_at).toLocaleDateString()}</p>
              <p className="text-muted">Last updated</p>
              <p>{new Date(user.updated_at).toLocaleDateString()}</p>
              <p className="text-muted">Last sign in</p>
              <p>
                {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : "Never"}
              </p>
            </div>
            {user.activity_logs.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-medium">Recent Activity</h3>
                <div className="max-h-48 space-y-1 overflow-y-auto">
                  {user.activity_logs.map((entry) => (
                    <div key={entry.id} className="flex justify-between text-xs">
                      <span>
                        <span className="font-medium">{entry.action}</span>
                        {entry.detail && <span className="text-muted">: {entry.detail}</span>}
                      </span>
                      <span className="text-muted whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
